import {
  parseAdvancedFlightCsvWithDeps,
} from "./advanced-flight.js";
import { createSourceCapture, csvSourceDocument } from "./source-data.js";
import { gpsPositionSparsityWarning } from "./data-prep.js";
import {
  createCanonicalParserEnvelope,
} from "../core/schema.js";
import {
  parseDynonSkyViewCsvWithDeps,
} from "./dynon.js";
import {
  parseEiCgr30cCsvWithDeps,
  parseEiCgr30cLegacyCsvWithDeps,
  parseEiCgrCsvWithDeps,
} from "./ei-cgr.js";
import {
  parseEiMvpCsvWithDeps,
} from "./ei-mvp.js";
import {
  parseGarminTxiCsvWithDeps,
  parseGarminUserLogCsvWithDeps,
} from "./garmin.js";
import {
  parseInsightG4TwinCsvWithDeps,
  parseInsightG3CsvWithDeps,
  parseInsightCsvWithDeps,
} from "./insight.js";
import {
  parseJpiCsvWithDeps,
} from "./jpi.js";

export const extractedParserIds = Object.freeze([
  "advanced-flight",
  "ei-cgr",
  "ei-cgr-30c",
  "ei-cgr-30c-legacy",
  "ei-mvp",
  "insight",
  "insight-g3",
  "insight-g4-twin",
  "dynon-skyview",
  "garmin-user-log",
  "garmin-g1000",
  "garmin-txi",
  "jpi-csv",
]);

// Every parser version below retains original sources and pre-normalization
// samples so cleanup and aggregation no longer discard source measurements.
export const extractedParserVersions = Object.freeze({
  // v4: records ambiguous display-unit values without guessing canonical units.
  "advanced-flight": "advanced-flight/v4",
  // v5: preserves auxiliary channels and validates local clock observations.
  // v6: strict hemisphere parsing, held GPS positions blanked while moving,
  // implausible jumps removed, sparse-position warning.
  "ei-cgr": "ei-cgr/v6",
  "ei-cgr-30c": "ei-cgr-30c/v3",
  "ei-cgr-30c-legacy": "ei-cgr-30c-legacy/v4",
  "ei-mvp": "ei-mvp/v3",
  // Retain fuel channels and cylinder samples beyond the original short lists.
  "insight": "insight/v3",
  "insight-g3": "insight-g3/v4",
  "insight-g4-twin": "insight-g4-twin/v3",
  "dynon-skyview": "dynon-skyview/v5",
  "garmin-user-log": "garmin-user-log/v3",
  // v8: preserves recorded local-time gaps, fuel used, and GPS fix aliases.
  "garmin-g1000": "garmin-g1000/v8",
  "garmin-txi": "garmin-txi/v8",
  // v5: validates sparse declared units and preserves decoded source fields.
  "jpi-csv": "jpi-csv/v5",
});

export const EI_CGR_COMBO_PARSER_ID = "ei-cgr-combo";
// v8: retains both source logs and aligned samples with shared aggregation.
// v9: inherits the 30P GPS position clean-up (ei-cgr/v6).
export const EI_CGR_COMBO_PARSER_VERSION = "ei-cgr-combo/v9";

export const JPI_BINARY_PARSER_ID = "jpi-binary";
// v7: retains binary source metrics alongside canonical normalized channels.
export const JPI_BINARY_PARSER_VERSION = "jpi-binary/v7";

export function parserVersionForId(parserId) {
  if (parserId === "ei-cgr-twin") return "ei-cgr-twin/v2";
  if (parserId === EI_CGR_COMBO_PARSER_ID) return EI_CGR_COMBO_PARSER_VERSION;
  if (parserId === JPI_BINARY_PARSER_ID) return JPI_BINARY_PARSER_VERSION;
  return extractedParserVersions[parserId] ?? null;
}

const sixCylinderGroups = Object.freeze([1, 2, 3, 4, 5, 6]);
const twelveCylinderTwinGroups = Object.freeze([
  sixCylinderGroups,
  Object.freeze([7, 8, 9, 10, 11, 12]),
]);

export const extractedParserEngineLayouts = Object.freeze({
  "insight-g4-twin": Object.freeze({
    count: 2,
    engineIds: Object.freeze(["left", "right"]),
    cylindersPerEngine: Object.freeze([6, 6]),
    egtGroups: twelveCylinderTwinGroups,
    chtGroups: twelveCylinderTwinGroups,
  }),
});

export function parserEngineLayoutForId(parserId) {
  return extractedParserEngineLayouts[parserId] ?? null;
}

export function createExtractedParserEnvelope(parserId, points, options: Record<string, any> = {}) {
  return createCanonicalParserEnvelope(points, {
    ...options,
    parserId,
    parserVersion: options.parserVersion ?? parserVersionForId(parserId),
    engineLayout: options.engineLayout ?? parserEngineLayoutForId(parserId) ?? undefined,
  });
}

export function createExtractedParserRegistry(hooks: Record<string, any> = {}) {
  return new Map([
    ["advanced-flight", (text, entry: Record<string, any> = {}) => parseAdvancedFlightCsvWithDeps(text, entry, hooks)],
    ["ei-cgr", (text) => parseEiCgrCsvWithDeps(text, hooks)],
    ["ei-cgr-30c", (text) => parseEiCgr30cCsvWithDeps(text, hooks)],
    ["ei-cgr-30c-legacy", (text) => parseEiCgr30cLegacyCsvWithDeps(text, hooks)],
    ["ei-mvp", (text) => parseEiMvpCsvWithDeps(text, hooks)],
    ["insight", (text) => parseInsightCsvWithDeps(text, hooks)],
    ["insight-g3", (text) => parseInsightG3CsvWithDeps(text, hooks)],
    ["insight-g4-twin", (text) => parseInsightG4TwinCsvWithDeps(text, hooks)],
    ["dynon-skyview", (text, entry: Record<string, any> = {}) => parseDynonSkyViewCsvWithDeps(text, entry, hooks)],
    ["garmin-user-log", (text, entry: Record<string, any> = {}) => parseGarminUserLogCsvWithDeps(text, entry, hooks)],
    ["garmin-g1000", (text) => parseGarminTxiCsvWithDeps(text, hooks, "garmin-g1000")],
    ["garmin-txi", (text) => parseGarminTxiCsvWithDeps(text, hooks)],
    ["jpi-csv", (text) => parseJpiCsvWithDeps(text, hooks)],
  ]);
}

export function createExtractedParserEnvelopeRegistry(hooks: Record<string, any> = {}, options: Record<string, any> = {}) {
  return new Map(extractedParserIds.map((parserId) => {
    return [
      parserId,
      (...args) => {
        const capture = createSourceCapture(hooks);
        const parser = createExtractedParserRegistry(capture.hooks).get(parserId);
        const points = (parser as (...a: any[]) => any)(...args);
        const envelopeOptions = typeof options === "function" ? options(parserId) : options;
        // Sparse GPS updates (CGR-30P feeds) are worth a Data Quality note for
        // every parser; dense one-hertz tracks never trigger it.
        const sparsePosition = gpsPositionSparsityWarning(points);
        return createExtractedParserEnvelope(parserId, points, {
          ...envelopeOptions,
          parserWarnings: [...(envelopeOptions.parserWarnings ?? []), ...(sparsePosition ? [sparsePosition] : [])],
          ...(capture.engineLayout ? { engineLayout: capture.engineLayout } : {}),
          sourceData: capture.finish([csvSourceDocument(args[0], parserId, args[1] ?? {})]),
        });
      },
    ];
  }));
}
